import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";
import Switch from "@material-ui/core/Switch";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import InputAdornment from "@material-ui/core/InputAdornment";
import ColorizeIcon from "@material-ui/icons/Colorize";
import CheckIcon from "@material-ui/icons/Check";
import { ColorBox } from "material-ui-color";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import LogoLoader from "../LogoLoader";
import { FormDialogHeader, FormSection, useFormDialogStyles } from "../FormDialog";

// cores sugeridas (todas com contraste bom para texto branco)
const PRESET_COLORS = [
  "#2D7BE8", "#1565C0", "#00897B", "#2E7D32", "#7CB342",
  "#F9A825", "#EF6C00", "#D84315", "#C62828", "#AD1457",
  "#6A1B9A", "#4527A0", "#5D4037", "#546E7A", "#37474F",
];

const HEX_REGEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

const useStyles = makeStyles((theme) => ({
  swatches: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  swatch: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    border: 0,
    padding: 0,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    outlineOffset: 2,
    transition: "transform .1s",
    "&:hover": { transform: "scale(1.08)" },
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}` },
  },
  swatchSelected: {
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${theme.palette.text.primary}`,
  },
  customSwatch: {
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.text.secondary,
  },
  colorDot: {
    width: 18,
    height: 18,
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
  },
  colorBox: {
    alignSelf: "center",
    marginTop: theme.spacing(1),
  },
  preview: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: 10,
    border: `1px dashed ${theme.palette.divider}`,
  },
  previewChip: {
    color: "#fff",
    fontWeight: 600,
    fontSize: 12,
    borderRadius: 12,
    height: 24,
  },
  kanbanRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
  },
  kanbanTitle: {
    fontWeight: 600,
    fontSize: 14,
  },
}));

const TagSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .min(3, "Use pelo menos 3 caracteres")
    .required("Informe o nome da etiqueta"),
  color: Yup.string().matches(HEX_REGEX, {
    message: "Use uma cor no formato #RRGGBB",
    excludeEmptyString: true,
  }),
});

const TagModal = ({ open, onClose, tagId, reload }) => {
  const classes = useStyles();
  const dialogClasses = useFormDialogStyles();
  const { user } = useContext(AuthContext);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);

  const initialState = {
    name: "",
    color: PRESET_COLORS[0],
    kanban: 0,
  };

  const [tag, setTag] = useState(initialState);
  const [loadingData, setLoadingData] = useState(false);
  const canSetKanban = user.profile === "admin" || user.profile === "supervisor";

  useEffect(() => {
    (async () => {
      if (!tagId) return;
      setLoadingData(true);
      try {
        const { data } = await api.get(`/tags/${tagId}`);
        setTag((prevState) => {
          return { ...prevState, ...data, color: data.color || prevState.color };
        });
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingData(false);
      }
    })();
  }, [tagId, open]);

  const handleClose = () => {
    setTag(initialState);
    setColorPickerOpen(false);
    onClose();
  };

  const handleSaveTag = async (values) => {
    const tagData = {
      ...values,
      name: values.name.trim(),
      userId: user.id,
      kanban: values.kanban ? 1 : 0,
    };
    try {
      if (tagId) {
        await api.put(`/tags/${tagId}`, tagData);
      } else {
        await api.post("/tags", tagData);
      }
      toast.success(i18n.t("tagModal.success"));
      if (typeof reload == "function") {
        reload();
      }
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      scroll="paper"
      classes={{ paper: dialogClasses.paper }}
      aria-labelledby="tag-modal-title"
    >
      {loadingData && <LogoLoader overlay />}
      <Formik
        initialValues={tag}
        enableReinitialize={true}
        validationSchema={TagSchema}
        onSubmit={async (values) => {
          // Aguarda a requisição terminar: enquanto isso o Formik mantém
          // isSubmitting=true e o botão desabilitado, evitando envio duplicado.
          await handleSaveTag(values);
        }}
      >
        {({ touched, errors, isSubmitting, values, setFieldValue }) => {
          const color = HEX_REGEX.test(values.color || "") ? values.color : "#999999";
          const isPreset = PRESET_COLORS.some(
            (c) => c.toLowerCase() === (values.color || "").toLowerCase()
          );
          return (
            <Form style={{ display: "contents" }}>
              <FormDialogHeader
                id="tag-modal-title"
                title={tagId ? "Editar tag" : "Nova tag"}
                subtitle="Etiquetas ajudam a organizar e filtrar contatos e atendimentos."
                onClose={handleClose}
                disabled={isSubmitting}
              />

              <DialogContent dividers className={dialogClasses.content}>
                <FormSection title="Nome">
                  <Field
                    as={TextField}
                    autoFocus
                    name="name"
                    placeholder="Ex.: Cliente VIP"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    inputProps={{ "aria-label": "Nome da etiqueta", maxLength: 60 }}
                  />
                </FormSection>

                <FormSection title="Cor">
                  <div className={classes.swatches} role="radiogroup" aria-label="Cor da etiqueta">
                    {PRESET_COLORS.map((c) => {
                      const selected = c.toLowerCase() === (values.color || "").toLowerCase();
                      return (
                        <button
                          key={c}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          aria-label={c}
                          className={`${classes.swatch} ${selected ? classes.swatchSelected : ""}`}
                          style={{ backgroundColor: c }}
                          onClick={() => setFieldValue("color", c)}
                        >
                          {selected && <CheckIcon style={{ fontSize: 18 }} />}
                        </button>
                      );
                    })}
                    <Tooltip title="Escolher outra cor">
                      <button
                        type="button"
                        aria-label="Escolher outra cor"
                        className={`${classes.swatch} ${classes.customSwatch} ${
                          !isPreset ? classes.swatchSelected : ""
                        }`}
                        style={!isPreset ? { backgroundColor: color, color: "#fff" } : undefined}
                        onClick={() => setColorPickerOpen((v) => !v)}
                      >
                        <ColorizeIcon style={{ fontSize: 18 }} />
                      </button>
                    </Tooltip>
                  </div>
                  {colorPickerOpen && (
                    <>
                      <Field
                        as={TextField}
                        name="color"
                        label="Código da cor"
                        error={touched.color && Boolean(errors.color)}
                        helperText={touched.color && errors.color}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <span className={classes.colorDot} style={{ backgroundColor: color }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <div className={classes.colorBox}>
                        <ColorBox
                          disableAlpha={true}
                          hslGradient={false}
                          value={color}
                          onChange={(val) => setFieldValue("color", `#${val.hex}`)}
                        />
                      </div>
                    </>
                  )}
                  <div className={classes.preview}>
                    <span className={dialogClasses.muted} style={{ fontSize: 13 }}>
                      Pré-visualização:
                    </span>
                    <Chip
                      size="small"
                      label={values.name?.trim() || "Nome da etiqueta"}
                      className={classes.previewChip}
                      style={{ backgroundColor: color }}
                    />
                  </div>
                </FormSection>

                {canSetKanban && (
                  <FormSection title="Kanban">
                    <label className={classes.kanbanRow}>
                      <div>
                        <Typography className={classes.kanbanTitle}>
                          Usar como etapa do Kanban
                        </Typography>
                        <Typography variant="body2" className={dialogClasses.muted}>
                          A tag vira uma coluna no Kanban de atendimentos e deixa de aparecer
                          como tag de contato.
                        </Typography>
                      </div>
                      <Switch
                        color="primary"
                        checked={values.kanban === 1 || values.kanban === true}
                        onChange={(e) => setFieldValue("kanban", e.target.checked ? 1 : 0)}
                        inputProps={{ "aria-label": "Usar como etapa do Kanban" }}
                      />
                    </label>
                  </FormSection>
                )}
              </DialogContent>

              <DialogActions className={dialogClasses.actions}>
                <Button
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className={`${dialogClasses.actionButton} ${dialogClasses.cancelButton}`}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  disableElevation
                  className={`${dialogClasses.actionButton} ${dialogClasses.progressWrapper}`}
                >
                  {tagId ? "Salvar alterações" : "Criar tag"}
                  {isSubmitting && (
                    <CircularProgress size={24} className={dialogClasses.buttonProgress} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default TagModal;
